
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { getSubscriptionPlanByTier } from "./types";
import { getCurrentSubscription } from "./subscriptionQueries";

// Get current month's lead imports for the user
export const getCurrentMonthLeadImports = async (): Promise<number> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return 0;

  try {
    const currentMonth = new Date().toISOString().slice(0, 7); // Format: YYYY-MM
    
    // Use a standard query instead of RPC
    const { data, error } = await supabase
      .from('lead_imports')
      .select('count')
      .eq('user_id', session.user.id)
      .eq('month_year', currentMonth)
      .maybeSingle();

    if (error) {
      console.error("Error fetching lead imports:", error);
      return 0;
    }

    return data?.count || 0;
  } catch (error) {
    console.error("Error in getCurrentMonthLeadImports:", error);
    return 0;
  }
};

// Check if user has quota available for importing leads
export const checkUserLeadQuota = async (): Promise<boolean> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return false;

  try {
    // Call database function to check quota
    const { data, error } = await supabase.rpc(
      'user_has_lead_quota',
      { user_uuid: session.user.id }
    );
    
    if (error) {
      console.error("Error checking lead quota:", error);
      return false;
    }
    
    if (!data) {
      toast({
        title: "Import limit reached",
        description: "You've reached your monthly import limit. Please upgrade your subscription for more imports.",
        variant: "destructive",
      });
    }
    
    return !!data;
  } catch (error) {
    console.error("Error in checkUserLeadQuota:", error);
    return false;
  }
};

// Atomically check quota and increment import count in a single DB call.
// Returns true if quota was available and the counter was incremented.
// Returns false if at/over limit (no increment happens).
export const trackLeadImport = async (): Promise<boolean> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return false;

  try {
    const { data, error } = await supabase.rpc(
      'try_increment_lead_import',
      { user_uuid: session.user.id }
    );

    if (error) {
      console.error("Error in atomic lead import:", error);
      return false;
    }

    if (!data) {
      toast({
        title: "Import limit reached",
        description: "You've reached your monthly import limit. Please upgrade your subscription for more imports.",
        variant: "destructive",
      });
    }

    return !!data;
  } catch (error) {
    console.error("Error in trackLeadImport:", error);
    return false;
  }
};
